import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { gozokiApiWithAuth } from '../gozokiApi'
import { saveAutoComputeStockMinimum } from '../mutations/stores.mutations'
import {
  exportReapproSimulationAllStoresXlsx,
  exportReapproSimulationXlsx,
  fetchAntiGaspi,
  fetchAutoComputeStockMinimum,
  fetchBoutiques,
  fetchCartsDatas,
  fetchFridgeStores,
  fetchPromoData,
  fetchPromoProducts,
  fetchStore,
  fetchStoreArticles,
  fetchStoreCategoryProducts,
  fetchStoreCommandNumber,
  fetchStoreCpiKiosks,
  fetchStoreNewProducts,
  fetchStoreProductArticles,
  fetchStoreProducts,
  fetchStoreStock,
  fetchStores,
  fetchStoresStocks,
  fetchStoresmMetaDatas,
  fetchTodayDatas,
  updateOneMinStock,
} from '../queries/stores.queries'

export const useStores = () =>
  useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
  })

export const useFridgeStores = () =>
  useQuery({
    queryKey: ['fridgeStores'],
    queryFn: fetchFridgeStores,
  })

export const useBoutiques = () =>
  useQuery({
    queryKey: ['stores', 'boutiques'],
    queryFn: () => fetchBoutiques(),
  })

export const useStore = (id?: number) =>
  useQuery({
    queryKey: ['store', id],
    queryFn: id
      ? () => {
          if (Number.isNaN(id)) {
            return null
          }
          return fetchStore(id)
        }
      : skipToken,
  })

export const useStoreArticles = (id: number) =>
  useQuery({
    queryKey: ['store', id, 'articles'],
    queryFn: () => {
      if (Number.isNaN(id)) {
        return []
      }
      return fetchStoreArticles(id)
    },
  })

export const useAntiGaspi = (id?: number) =>
  useInfiniteQuery({
    queryKey: ['store', id, 'antigaspi'],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (Number.isNaN(id) || !id) {
        return { data: [], hasMorePage: false }
      }
      return fetchAntiGaspi(id, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const usePromoProducts = (id?: number) =>
  useInfiniteQuery({
    queryKey: ['store', id, 'promo-products'],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (Number.isNaN(id) || !id) {
        return { data: [], hasMorePage: false }
      }
      return fetchPromoProducts(id, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const useStoreCategoryProducts = (id: number, cat: number) =>
  useInfiniteQuery({
    queryKey: ['store', id, 'category-products', cat],
    queryFn: ({ pageParam = 1 }: { pageParam?: number }) => {
      if (Number.isNaN(id)) {
        return { data: [], hasMorePage: false }
      }
      return fetchStoreCategoryProducts(id, cat, pageParam as number)
    },
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const useStoreProducts = (storeId: number) =>
  useQuery({
    queryKey: ['store', storeId, 'products'],
    queryFn: Number.isNaN(storeId) || storeId === 0 ? skipToken : () => fetchStoreProducts(storeId),
  })

export const useStoreNewProducts = (storeId: number) =>
  useInfiniteQuery({
    queryKey: ['store', storeId, 'new-products'],
    queryFn:
      Number.isNaN(storeId) || storeId === 0
        ? skipToken
        : ({ pageParam = 1 }: { pageParam?: number }) =>
            fetchStoreNewProducts(storeId, pageParam as number),
    initialPageParam: 1,
    getNextPageParam: (_lastPage, allPages) => allPages.length + 1,
  })

export const useStoreStock = (id: number) =>
  useQuery({
    queryKey: ['store', id, 'stock'],
    queryFn: Number.isNaN(id) || id === 0 ? skipToken : () => fetchStoreStock(id),
  })

export const useStoresStocks = (ids: number[] | undefined) =>
  useQuery({
    queryKey: ['stores', 'stocks', ids],
    queryFn: async () => {
      if (!ids || ids.length === 0) {
        return [
          {
            metadata: {
              articleCount: 0,
              antiGaspiArticleCount: 0,
              expiredArticleCount: 0,
              totalStockValue: 0,
              totalAntiGaspiStockValue: 0,
              totalExpiredStockValue: 0,
            },
            rows: [],
          },
        ]
      }
      return fetchStoresStocks(ids)
    },
  })
export const useStoresMetadata = (ids: number[] | undefined) =>
  useQuery({
    queryKey: ['stores', 'metadata', ids],
    queryFn: async () => {
      if (!ids || ids.length === 0) {
        return [
          {
            metadata: {
              articleCount: 0,
              antiGaspiArticleCount: 0,
              expiredArticleCount: 0,
              totalStockValue: 0,
              totalAntiGaspiStockValue: 0,
              totalExpiredStockValue: 0,
            },
          },
        ]
      }
      return fetchStoresmMetaDatas(ids)
    },
  })

export const useStoreProductArticles = (storeId: number, productId: number) =>
  useQuery({
    queryKey: ['store', storeId, 'product', productId],
    queryFn: () => {
      if (Number.isNaN(storeId) || Number.isNaN(productId)) {
        return []
      }
      return fetchStoreProductArticles(storeId, productId)
    },
  })

export const useStoreTodayData = (id: number) =>
  useQuery({
    queryKey: ['store', id, 'todayData'],
    queryFn: () => {
      if (Number.isNaN(id)) {
        return null
      }
      return fetchTodayDatas(id)
    },
  })

export const useStoreCommandNumber = (id: number) =>
  useQuery({
    queryKey: ['store', id, 'commandNumber'],
    queryFn: () => {
      if (Number.isNaN(id)) {
        return 0
      }
      return fetchStoreCommandNumber(id)
    },
  })

export const useStoreKiosks = (storeReference: string | null) =>
  useQuery({
    queryKey: ['store', storeReference, 'kiosks'],
    queryFn: () => {
      if (!storeReference) {
        return []
      }
      return fetchStoreCpiKiosks(storeReference)
    },
  })

export const useStorePromoData = (dateStart: string, dateEnd: string, ids: number[]) =>
  useQuery({
    queryKey: ['store', 'promoData', dateStart, dateEnd, ids],
    queryFn: () => fetchPromoData(dateStart, dateEnd, ids),
  })

export const usePaginateCarts = (
  storeId: number,
  page: number | null,
  pageSize: number | null,
  startDate: string | null,
  endDate: string | null,
  cartStatus: string | null,
  sortBy?: 'created_at' | 'updated_at',
  sortDirection?: 'asc' | 'desc',
) =>
  useQuery({
    queryKey: [
      'store',
      storeId,
      'carts',
      page,
      pageSize,
      startDate,
      endDate,
      cartStatus,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      fetchCartsDatas(
        page,
        pageSize,
        startDate,
        endDate,
        cartStatus,
        storeId,
        sortBy,
        sortDirection,
      ),
    placeholderData: keepPreviousData,
  })

export const useUpdateOneMinStock = () =>
  useMutation({
    mutationFn: updateOneMinStock,
  })

export const useUpdateStoreCategoryMinCompute = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: saveAutoComputeStockMinimum,
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({
        queryKey: ['store', input.storeId, 'categories-minimum-stock-computes'],
      })
    },
  })
}

export const useStoreCategoriesMinimumStockComputes = (storeId: number) =>
  useQuery({
    queryKey: ['store', storeId, 'categories-minimum-stock-computes'],
    queryFn: () => fetchAutoComputeStockMinimum({ storeId }),
  })

export const useReapprosExport = () =>
  useMutation({
    mutationFn: async () => {
      const res = await gozokiApiWithAuth.get<Blob>('/stores/reappro-needs/export-xlsx', {
        responseType: 'blob',
      })

      return res.data
    },
  })

export const useReapproSimulationExport = () =>
  useMutation({
    mutationFn: (storeId: number) => exportReapproSimulationXlsx(storeId),
  })

export const useReapproSimulationAllStoresExport = () =>
  useMutation({
    mutationFn: () => exportReapproSimulationAllStoresXlsx(),
  })

export const useReapprosImport = () =>
  useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('importFile', file)

      const res = await gozokiApiWithAuth.post('/stores/reappro-needs/import-xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
  })
